import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { isPresentationProject } from "@utils/presentationMode";

import DisbursementHeader from "./DisbursementHeader";
import DisbursementList from "./DisbursementList";
import ResourceCatalog from "./ResourceCatalog";
import GroupEditorOffcanvas from "./GroupEditorOffcanvas";
import RuleEditorOffcanvas, { normalizeTarget } from "./RuleEditorOffcanvas";
import OrganizeWithAiPanel from "./ai/OrganizeWithAiPanel";
import {
  fetchDisbursementRules,
  normalizeDisbursementList,
  patchDisbursementRule,
  updateDisbursementAdjustment,
} from "./disbursementApi";
import { isAiSource } from "./format";

// 202 = o motor está refazendo a curva em segundo plano. A tela não fica esperando:
// avisa e relê uma vez depois desta janela, quando o estado novo já deve estar no ar.
const REGENERATION_RECHECK_MS = 2000;

// Only known protocol codes may be exposed in diagnostic DOM attributes.
const TOGGLE_ERROR_CODES = new Set([
  "rule_not_found",
  "network_error",
  "invalid_patch",
  "rule_required",
  "project_scope_required",
  "presentation_read_only",
  "unauthorized",
  "forbidden",
  "internal_error",
  "regeneration_failed",
  "source_unavailable",
  "revision_conflict",
]);
const toggleDiagnostics = (result) => ({
  httpStatus:
    Number.isInteger(result?.code) &&
    (result.code === 0 || (result.code >= 100 && result.code <= 599))
      ? result.code
      : undefined,
  errorCode:
    typeof result?.errorCode === "string" &&
    TOGGLE_ERROR_CODES.has(result.errorCode)
      ? result.errorCode
      : undefined,
});

// `?editRule=<chave do alvo>` é o link de volta do "Ajustar regra" do offcanvas do
// item (spec 8.4): a chave é a do ALVO da regra (`resource_group:erp:...`,
// `resource:...`, `node:...`), que é o que o motor publica em `contribution.ruleKey`.
export const EDIT_RULE_PARAM = "editRule";

// Chave de alvo -> alvo mínimo pro editor abrir. Só a chave chega pelo link, então o
// `kind` é derivado do prefixo dela (mesmo vocabulário de `engine.js#ruleKeys`) e o
// `level` é o de ARMAZENAMENTO — o nível de consulta é rederivado no fetch.
const targetFromRuleKey = (key) => {
  const text = String(key ?? "");
  if (text.startsWith("resource_group:")) {
    return {
      kind: "resource_group",
      key: text,
      level: "resource_group",
      label: text,
    };
  }
  if (text.startsWith("resource:")) {
    return { kind: "resource", key: text, level: "resource", label: text };
  }
  if (text.startsWith("node:")) {
    // O nível do nó não vem na chave; `substep` é o padrão do picker e o editor deixa
    // o cliente trocar antes de salvar.
    return {
      kind: "node",
      key: text,
      level: "substep",
      label: text.slice("node:".length),
    };
  }
  return null;
};

const Disbursement = () => {
  const { activeProject } = useProjectContext();
  const { selectProject } = useProjectActions();
  const [searchParams, setSearchParams] = useSearchParams();
  const workId = activeProject?._id ?? null;
  const [notice, setNotice] = useState(null);
  const [toggleDiagnostic, setToggleDiagnostic] = useState(null);
  const [filters, setFilters] = useState({ targetKind: "all", source: "all" });
  const [items, setItems] = useState([]);
  const [listMetadata, setListMetadata] = useState(null);
  const [catalogSeed, setCatalogSeed] = useState(0);
  const [catalogClassification, setCatalogClassification] = useState("all");
  const [catalogReload, setCatalogReload] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(false);
  // "Uma resposta da rota já chegou (ou falhou) para esta obra". `listLoading` NÃO
  // serve para isso: ele nasce `false` e só vira `true` num efeito, então no primeiro
  // render um `?editRule=` veria uma lista vazia que nem foi pedida ainda e abriria o
  // editor pelo plano B — perdendo o perfil gravado da regra.
  const [listSettled, setListSettled] = useState(false);
  // `{ target, rule }` quando o editor está aberto; `null` fechado. O alvo entra já
  // NORMALIZADO (a identidade que o editor segue é a chave, não o objeto).
  const [editor, setEditor] = useState(null);
  // `{ group }` quando o editor de grupo Ampli está aberto (`group: null` = novo).
  const [groupEditor, setGroupEditor] = useState(null);
  // Painel "Organizar com IA" (Task 10): só existe montado, então o `false` do
  // botão nunca aparece pra quem está em modo apresentação (o botão que abre nem
  // renderiza sob `readOnly`).
  const [organizeOpen, setOrganizeOpen] = useState(false);
  // Guarda contra resposta desatualizada: cada chamada de `loadList` reserva o
  // próximo número de geração; se, quando o `await` volta, uma geração mais nova
  // já foi disparada (ex.: o usuário trocou de obra no meio do caminho), esta
  // resposta é descartada em vez de sobrescrever o estado com dado velho.
  const loadGeneration = useRef(0);
  const recheckTimer = useRef(null);

  // Timer de releitura do 202 nunca sobrevive à desmontagem nem à troca de obra:
  // `setState` depois disso seria atualização em componente morto.
  useEffect(() => () => clearTimeout(recheckTimer.current), []);

  useEffect(() => {
    setNotice(null);
    setEditor(null);
    setGroupEditor(null);
    setOrganizeOpen(false);
    clearTimeout(recheckTimer.current);
  }, [workId]);

  // A rota `GET .../disbursement/rules` não filtra por query (ver ruling FE6 em
  // `disbursementApi.js`): `filters` é aplicado aqui, client-side, sobre a lista
  // já normalizada, então uma mudança de filtro nunca precisa recarregar do servidor.
  const loadList = useCallback(async () => {
    loadGeneration.current += 1;
    const generation = loadGeneration.current;
    if (!workId) {
      setItems([]);
      setListSettled(false);
      return;
    }
    setListSettled(false);
    setListLoading(true);
    setListError(false);
    const payload = await fetchDisbursementRules(workId);
    if (generation !== loadGeneration.current) return;
    if (!payload) {
      setItems([]);
      setListMetadata(null);
      setListError(true);
      setListLoading(false);
      setListSettled(true);
      return;
    }
    const normalized = normalizeDisbursementList(payload);
    setItems(normalized.items);
    setListMetadata({
      workId,
      state: normalized.state
        ? { ...normalized.state, coverage: normalized.coverage }
        : null,
      resourceSummary: normalized.resourceSummary,
    });
    setCatalogReload((current) => current + 1);
    setListLoading(false);
    setListSettled(true);
  }, [workId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Alvos de `group`/`erp_group_without_rule` não têm `target.kind`, mas
  // representam sempre um grupo de recurso — contam para o filtro "Grupos"
  // (`resource_group`) e ficam escondidos sob "Insumos"/"Níveis da EAP".
  // Ajustes de mês não são alvos: passam por qualquer filtro de tipo de alvo.
  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        const matchesTarget =
          filters.targetKind === "all" ||
          item.kind === "adjustment" ||
          (item.kind === "rule" && item.target?.kind === filters.targetKind) ||
          ((item.kind === "group" || item.kind === "erp_group_without_rule") &&
            filters.targetKind === "resource_group");
        const matchesSource =
          filters.source === "all" ||
          item.kind !== "rule" ||
          (filters.source === "ai"
            ? isAiSource(item.source)
            : !isAiSource(item.source));
        return matchesTarget && matchesSource;
      }),
    [items, filters],
  );

  // A regra gravada de cada linha, por `ruleId`: escolher no editor um alvo que já
  // tem regra tem que carregar a forma GRAVADA, não o padrão à vista (espec. 4.1).
  const rulesByRuleId = useMemo(
    () =>
      Object.fromEntries(
        items
          .filter((item) => item.kind === "rule" && item.ruleId)
          .map((item) => [item.ruleId, item]),
      ),
    [items],
  );

  // Chegada por `?editRule=`: abre o editor na regra pedida e LIMPA o parâmetro, pra
  // fechar o offcanvas e voltar não reabri-lo. Espera a lista carregar antes de
  // decidir — assim a linha gravada (com perfil e descrição) ganha do alvo mínimo
  // montado a partir da chave, que é só o plano B pra uma chave que não está na lista
  // (alvo herdado, regra inativa filtrada, obra sem cobertura ainda).
  const editRuleKey = searchParams.get(EDIT_RULE_PARAM);
  useEffect(() => {
    if (!editRuleKey || !listSettled) return;
    const row = items.find(
      (item) => item.kind === "rule" && item.target?.key === editRuleKey,
    );
    const seed = row
      ? {
          target: normalizeTarget({
            ...row.target,
            plannedCents: row.coveredCents,
          }),
          rule: row,
        }
      : { target: normalizeTarget(targetFromRuleKey(editRuleKey)), rule: null };
    if (!seed.target) return;
    setNotice(null);
    setEditor(seed);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(EDIT_RULE_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [editRuleKey, listSettled, items, setSearchParams]);

  if (!workId) return null;
  const readOnly = isPresentationProject(activeProject);

  const refresh = async (result, { recheck = false } = {}) => {
    const refreshed = await selectProject(workId);
    if (!refreshed) {
      // Recheque do 202 que não volta NÃO é "falha": a gravação deu certo e a curva
      // pode muito bem estar pronta. O aviso continua no estado neutro de
      // regeneração em vez de acusar um erro que não houve.
      if (!recheck) setNotice("falha");
      return;
    }
    if (result?.regeneration?.status === "regeneration_failed")
      setNotice("regraGravadaCurvaFalha");
    else if (result?.code === 202 || recheck) setNotice("regenerando");
    else setNotice("atualizada");
    await loadList();
    if (
      result?.code === 202 &&
      result?.regeneration?.status !== "regeneration_failed"
    ) {
      clearTimeout(recheckTimer.current);
      recheckTimer.current = setTimeout(() => {
        refresh({ code: 200 }, { recheck: true });
      }, REGENERATION_RECHECK_MS);
    }
  };

  // Depois de um 202 quem diz se a curva ficou pronta é o ESTADO do módulo na obra
  // recarregada, não o código HTTP da escrita: derivar no render mantém o aviso
  // honesto mesmo se o recheque chegar antes do motor terminar.
  const regenerationText =
    activeProject?.ampliDisbursement?.state === "ready"
      ? "Curva atualizada."
      : "Atualizando a curva…";

  const noticeText = {
    regenerando: regenerationText,
    atualizada: "Curva atualizada.",
    falha: "Não foi possível atualizar a página com a curva mais recente.",
    escritaFalha: "Não foi possível salvar a alteração. Tente novamente.",
    toggleFalha: "Não foi possível salvar a alteração. Tente novamente.",
    regraGravadaCurvaFalha:
      "A regra foi salva, mas a curva não pôde ser atualizada. Atualize a curva sem enviar a regra novamente.",
  }[notice];
  const noticeIsFailure =
    notice === "falha" || notice === "escritaFalha" || notice === "toggleFalha";

  // As duas escritas recheca(m) o somente-leitura na hora de chamar a rota: os botões
  // só existem com `readOnly === false`, mas a obra pode virar de apresentação entre
  // a pintura da lista e o clique (troca de obra, `selectProject` que volta com outro
  // `read_only`). O back-end também barra (403 `presentation_read_only`) — este é o
  // portão de perto, que evita a requisição.
  const toggleRule = async (item, active) => {
    if (readOnly) return;
    setToggleDiagnostic(null);
    const result = await patchDisbursementRule(workId, item.ruleId, { active });
    if (!result || (result.code !== 200 && result.code !== 202)) {
      setToggleDiagnostic(toggleDiagnostics(result));
      setNotice("toggleFalha");
      return;
    }
    await refresh(result);
  };

  const removeAdjustment = async (item) => {
    if (readOnly) return;
    const result = await updateDisbursementAdjustment(workId, {
      operation: "remove",
      wbsCodeId: item.wbsCodeId,
      entryId: item.entryId,
      fromCompetence: item.fromCompetence,
      toCompetence: item.toCompetence,
    });
    if (!result || (result.code !== 200 && result.code !== 202)) {
      setNotice("escritaFalha");
      return;
    }
    await refresh(result);
  };

  return (
    <div className="ampli-disbursement-page">
      <DisbursementHeader
        moduleState={
          listMetadata?.workId === workId ? listMetadata.state : null
        }
        project={activeProject}
        readOnly={readOnly}
        onNewRule={() => {
          setNotice(null);
          setEditor({ target: null, rule: null });
        }}
        onNewGroup={() => {
          setNotice(null);
          setGroupEditor({ group: null });
        }}
        onOrganizeWithAi={() => {
          setNotice(null);
          setOrganizeOpen(true);
        }}
        onRegenerated={refresh}
      />
      {notice ? (
        <p
          className={`alert ${noticeIsFailure ? "alert-error" : "alert-info"}`}
          role={noticeIsFailure ? "alert" : "status"}
          data-http-status={
            notice === "toggleFalha" ? toggleDiagnostic?.httpStatus : undefined
          }
          data-error-code={
            notice === "toggleFalha" ? toggleDiagnostic?.errorCode : undefined
          }
        >
          {noticeText}
        </p>
      ) : null}
      {listMetadata?.workId === workId &&
      listMetadata.resourceSummary?.eligibleUnclassified > 0 ? (
        <p role="status">
          {`${listMetadata.resourceSummary.eligibleUnclassified.toLocaleString(
            "pt-BR",
          )} insumos com previsto disponível estão sem classificação ERP. Eles continuam no catálogo; nenhum grupo foi inventado.`}
          <button
            type="button"
            onClick={() => {
              setCatalogClassification("unclassified");
              setCatalogSeed((current) => current + 1);
              setFilters({ targetKind: "resource", source: "all" });
            }}
          >
            Ver insumos sem classificação
          </button>
        </p>
      ) : null}
      <DisbursementList
        catalogMode={filters.targetKind === "resource"}
        workId={workId}
        onSuggestion={(proposal) => {
          if (readOnly || proposal.workId !== workId) return;
          const existingRule =
            items.find(
              (item) =>
                item.kind === "rule" &&
                item.target?.key === proposal.target.key,
            ) ?? null;
          setEditor({
            target: normalizeTarget(proposal.target),
            rule: existingRule,
            proposal,
          });
        }}
        items={filteredItems}
        loading={listLoading}
        error={listError}
        readOnly={readOnly}
        filters={filters}
        onFiltersChange={(next) => {
          setCatalogClassification("all");
          setFilters(next);
        }}
        onEditRule={(item) =>
          setEditor({
            // A linha da lista não traz `plannedSeries`: o editor busca a série do alvo
            // antes de pré-visualizar (nunca desenha uma curva de zeros no lugar).
            target: normalizeTarget({
              ...item.target,
              plannedCents: item.coveredCents,
            }),
            rule: item,
          })
        }
        onEditGroup={(item) => setGroupEditor({ group: item })}
        onDefineRule={(target) =>
          setEditor({ target: normalizeTarget(target), rule: null })
        }
        onToggleRule={toggleRule}
        onRemoveAdjustment={removeAdjustment}
      />
      {filters.targetKind === "resource" ? (
        <ResourceCatalog
          key={`${workId}:${catalogSeed}`}
          workId={workId}
          readOnly={readOnly}
          reloadKey={catalogReload}
          initialClassification={catalogClassification}
          rulesByRuleId={rulesByRuleId}
          onDefineRule={(target) => {
            if (readOnly || target.isEligible !== true) return;
            setEditor({
              target: normalizeTarget(target),
              rule: target.ruleId ? rulesByRuleId[target.ruleId] ?? null : null,
            });
          }}
        />
      ) : null}

      {/* `key` por obra: o editor guarda alvo, forma e descrição em estado local, e
          nada disso pode sobreviver a uma troca de obra — remontar é o único jeito
          de garantir que não sobre a regra da obra anterior no formulário. */}
      <RuleEditorOffcanvas
        key={workId}
        open={Boolean(editor)}
        onClose={() => setEditor(null)}
        project={activeProject}
        projectId={workId}
        readOnly={readOnly}
        initialTarget={editor?.target ?? null}
        initialRule={editor?.rule ?? null}
        profile={editor?.proposal?.profile ?? null}
        initialSource={editor?.proposal?.source ?? "manual"}
        rulesByRuleId={rulesByRuleId}
        onSaved={refresh}
      />

      {/* Mesmo motivo do `key` acima: nome, critério e insumos escolhidos são estado
          local e não podem atravessar uma troca de obra. */}
      <GroupEditorOffcanvas
        key={`group-editor-${workId}`}
        open={Boolean(groupEditor)}
        onClose={() => setGroupEditor(null)}
        project={activeProject}
        projectId={workId}
        readOnly={readOnly}
        initialGroup={groupEditor?.group ?? null}
        onSaved={refresh}
      />

      {/* Só existe montado enquanto aberto: fechar/reabrir sempre pede um rascunho
          novo, e nunca sobrevive a uma troca de obra (efeito acima zera o estado). */}
      {organizeOpen ? (
        <OrganizeWithAiPanel
          workId={workId}
          onApplied={(result) => {
            setOrganizeOpen(false);
            refresh(result);
          }}
          onClose={() => setOrganizeOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default Disbursement;
